import React, { SyntheticEvent, useCallback, useEffect, useMemo } from 'react';

import { Autocomplete, Stack, TextField } from '@mui/material';

import { RuleCondition } from '~/consts/agentHub';

import { useGetCruiseLines } from '~/hooks/apis/cruises/useGetCruiseLines';

import {
  type CruiseLineOption,
  cruiseLineOptionToCondition,
  cruiseLineToOption,
  resolveCruiseLineOptions,
} from './cruiseLineOptions';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

function getCruiseLineOptionLabel(option: CruiseLineOption) {
  return option.code ? `${option.label} (${option.code})` : option.label;
}

export default function AgentHubCommissionsCruiseLineInput({ setCreatedConditions, existingConditions }: Props) {
  const { data: cruiseLines = [], isFetching, isError } = useGetCruiseLines();

  const selectedCruiseLines = useMemo(
    () => resolveCruiseLineOptions(existingConditions, cruiseLines),
    [existingConditions, cruiseLines],
  );

  // Repair recognised name-based values in the form so saving an existing rule stores codes.
  useEffect(() => {
    if (selectedCruiseLines.some((option, index) => option.value !== String(existingConditions[index].value))) {
      setCreatedConditions({ type: 'cruiseLine', value: selectedCruiseLines.map(cruiseLineOptionToCondition) }, true);
    }
  }, [existingConditions, selectedCruiseLines, setCreatedConditions]);

  const options = useMemo(() => {
    const byValue = new Map<string, CruiseLineOption>();

    for (const cruiseLine of cruiseLines) {
      const option = cruiseLineToOption(cruiseLine);
      byValue.set(option.value, option);
    }

    for (const selectedCruiseLine of selectedCruiseLines) {
      if (!byValue.has(selectedCruiseLine.value)) {
        byValue.set(selectedCruiseLine.value, selectedCruiseLine);
      }
    }

    return Array.from(byValue.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  }, [cruiseLines, selectedCruiseLines]);

  const handleChange = useCallback(
    (_: SyntheticEvent, next: Array<CruiseLineOption>) => {
      setCreatedConditions(
        {
          type: 'cruiseLine',
          value: next.map(cruiseLineOptionToCondition),
        },
        true,
      );
    },
    [setCreatedConditions],
  );

  const noOptionsText = useMemo(() => {
    if (isError) {
      return 'Failed to fetch cruise lines';
    }
    if (isFetching) {
      return 'Searching...';
    }
    return 'No cruise lines found';
  }, [isError, isFetching]);

  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        loading={isFetching}
        options={options}
        getOptionLabel={getCruiseLineOptionLabel}
        filterSelectedOptions
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Cruise lines"
            placeholder={selectedCruiseLines.length > 0 ? '' : 'Cruise lines'}
          />
        )}
        value={selectedCruiseLines}
        onChange={handleChange}
        noOptionsText={noOptionsText}
      />
    </Stack>
  );
}
